import { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { http, formatError } from "@/lib/api";

const KEY = "jarvis_widget_session";
const sid = localStorage.getItem(KEY) || crypto.randomUUID();
localStorage.setItem(KEY, sid);

const EN = {
  lang: "en",
  greeting: "Hello! I'm Jarvis, the Omvexis product assistant. Ask me about self-heating bowls, pads, specs, samples or shipping.",
  chips: ["Which product suits military rations?", "How fast can you send samples?", "I'd like a quote"],
  title: "Ask Jarvis", placeholder: "Type your question…", subtitle: "Omvexis product assistant · replies instantly", typing: "Jarvis is typing…",
};
const RTL = ["ar", "he", "fa", "ur"];
const visitorLang = () => new URLSearchParams(window.location.search).get("lang") || navigator.language || "en";

export default function Widget() {
  const [t, setT] = useState(EN);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: EN.greeting }]);
  useEffect(() => {
    http.get("/public/greeting", { params: { lang: visitorLang() } }).then((r) => {
      setT(r.data);
      setMsgs((m) => (m.length === 1 ? [{ role: "assistant", content: r.data.greeting }] : m));
    }).catch(() => {});
  }, []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await http.post("/public/chat", { session_id: sid, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, lead: data.lead_created }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: formatError(e) }]);
    }
    setBusy(false);
  };

  return (
    <div className="h-screen flex flex-col bg-[#1d0702] text-slate-100" dir={RTL.includes(t.lang) ? "rtl" : "ltr"} lang={t.lang} data-testid="widget-page">
      <header className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
        <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center"><img src="/omvexis-logo.png" alt="Omvexis" className="h-7" /></span>
        <div><div className="font-semibold text-sm" data-testid="widget-title">{t.title || "Ask Jarvis"}</div><div className="text-[11px] text-white/55" data-testid="widget-subtitle">{t.subtitle}</div></div>
      </header>
      <div className="flex-1 overflow-y-auto hud-scroll px-4 py-4 space-y-3" data-testid="widget-log">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed px-4 py-2.5 rounded-2xl ${m.role === "user" ? "bg-[#ffdcb8] text-[#2b0d02] rounded-br-md" : "bg-white/[0.06] border border-white/10 rounded-bl-md"}`} data-testid={`widget-msg-${i}`}>
              {m.content}
              {m.lead && <div className="mt-2 text-[11px] text-emerald-300" data-testid="widget-quote-sent">Enquiry sent to the Omvexis team</div>}
            </div>
          </div>
        ))}
        {busy && <div className="text-white/50 text-xs flex items-center gap-2" data-testid="widget-typing"><Loader2 size={12} className="animate-spin" /> {t.typing}</div>}
        {msgs.length === 1 && (
          <div className="flex flex-wrap gap-2">
            {t.chips.map((c, i) => <button key={c} onClick={() => send(c)} className="text-xs border border-white/15 rounded-full px-3 py-1.5 hover:bg-white/5 transition-colors" data-testid={`widget-chip-${i}`}>{c}</button>)}
          </div>
        )}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 p-3 border-t border-white/10">
        <input className="hud-input !rounded-full" placeholder={t.placeholder} value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} data-testid="widget-input" />
        <button className="hud-btn !px-4" disabled={busy || !input.trim()} data-testid="widget-send" aria-label="Send"><Send size={14} /></button>
      </form>
    </div>
  );
}
